import React from 'react';
import { Card, CardContent } from './ui/card';
import { ArrowsClockwise, CheckCircle, XCircle } from '@phosphor-icons/react';

/**
 * Progress banner for long-running push jobs (categories, terms, etc.).
 * Shows a live count + progress bar + currently-being-processed item.
 *
 * Props:
 *   job: { total, pushed, failed, status, current_name } | null
 *   label: e.g. "Pushing kategorier til WooCommerce" | "Pushing termer ..."
 */
export default function PushProgressBanner({ job, label }) {
  if (!job) return null;
  const total = job.total || 0;
  const done = (job.pushed || 0) + (job.failed || 0);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const isRunning = job.status === 'running';
  const isDone = job.status === 'done';

  return (
    <Card
      className={`border-2 ${isRunning ? 'border-primary bg-primary/5' : isDone ? 'border-success bg-success/5' : 'border-border'}`}
      data-testid={`push-progress-${job.kind || 'unknown'}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 flex-1 min-w-[280px]">
            <div className={`p-2 rounded-md shrink-0 ${isRunning ? 'bg-primary/15' : isDone ? 'bg-success/15' : 'bg-muted'}`}>
              {isRunning && <ArrowsClockwise size={20} weight="bold" className="text-primary animate-spin" />}
              {isDone && <CheckCircle size={20} weight="bold" className="text-success" />}
              {!isRunning && !isDone && <XCircle size={20} weight="bold" className="text-muted-foreground" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-sm font-bold leading-tight">{label}</p>
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                {isRunning && job.current_name
                  ? <>Behandler: <span className="text-foreground">{job.current_name}</span></>
                  : isDone
                    ? <>Ferdig — <strong>{job.pushed}</strong> ok{job.failed ? <>, <strong className="text-destructive">{job.failed}</strong> feilet</> : ''}</>
                    : 'Forbereder...'}
              </p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="font-heading text-2xl font-extrabold tabular-nums">
              {done}/{total}
            </p>
            {job.failed > 0 && (
              <p className="text-xs text-destructive">{job.failed} feilet</p>
            )}
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ease-out ${isDone && job.failed === 0 ? 'bg-success' : 'bg-primary'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </CardContent>
    </Card>
  );
}
